/*
 * Classic screens Maestro V2 shows inside its own shell. Reports & Analytics and
 * Configuration keep the pages they already have; only the address changes, so a
 * recruiter on V2 is not dropped into the Classic frame when they open one.
 *
 * A match stops at a path boundary. `/notifications` (message settings) must not
 * swallow `/notification` (the bell).
 */

const HOSTED = [
	{ classic: '/email-templates', v2: '/v2/configuration/templates' },
	{ classic: '/hot-candidates', v2: '/v2/configuration/hot-candidates' },
	{ classic: '/workflow-segments', v2: '/v2/configuration/workflow-segments' },
	{ classic: '/notifications', v2: '/v2/configuration/notifications' },
	{ classic: '/configuration', v2: '/v2/configuration' },
	{ classic: '/candidate-access', v2: '/v2/configuration/candidate-access' },
	{ classic: '/cadences', v2: '/v2/configuration/workflows' },
	{ classic: '/user_info', v2: '/v2/users' },
	{ classic: '/report', v2: '/v2/reports' },
	// Applicant sources (SPT-2326): one page, drawn on the V2 tokens, in either frame.
	{ classic: '/applicant-sources', v2: '/v2/applicant-sources' },
	{ classic: '/role', v2: '/v2/configuration/roles' },
	// The recruiter's own job-area alerts (SPT-2331), from the account menu in either version.
	{ classic: '/job-alerts', v2: '/v2/job-alerts' },
]

export const V2_REPORTS_PATH = '/v2/reports'
export const V2_APPLICANT_SOURCES_PATH = '/v2/applicant-sources'
export const V2_CONFIGURATION_PATH = '/v2/configuration'
export const V2_WORKFLOWS_PATH = '/v2/configuration/workflows'
export const V2_TEMPLATES_PATH = '/v2/configuration/templates'
export const V2_NOTIFICATION_SETTINGS_PATH = '/v2/configuration/notifications'
export const V2_HOT_CANDIDATES_PATH = '/v2/configuration/hot-candidates'
export const V2_SEGMENTS_PATH = '/v2/configuration/workflow-segments'
export const V2_ROLES_PATH = '/v2/configuration/roles'
export const V2_CANDIDATE_ACCESS_PATH = '/v2/configuration/candidate-access'
export const V2_JOB_ALERTS_PATH = '/v2/job-alerts'

const byLength = (key) => [...HOSTED].sort((a, b) => b[key].length - a[key].length)

const splitPath = (path = '') => {
	const hashAt = path.indexOf('#')
	const hash = hashAt >= 0 ? path.slice(hashAt) : ''
	const withoutHash = hashAt >= 0 ? path.slice(0, hashAt) : path
	const queryAt = withoutHash.indexOf('?')
	const query = queryAt >= 0 ? withoutHash.slice(queryAt) : ''
	let bare = queryAt >= 0 ? withoutHash.slice(0, queryAt) : withoutHash
	if (bare.length > 1 && bare.endsWith('/')) bare = bare.slice(0, -1)
	return { bare, query, hash }
}

const onBoundary = (path, prefix) => path === prefix || path.startsWith(`${prefix}/`)

export const isMaestroV2Path = (pathname = '') => pathname === '/v2' || pathname.startsWith('/v2/')

/**
 * The same Classic screen under /v2 when the recruiter is already in V2.
 * Anywhere else the path is returned as given, so Classic navigation is unchanged.
 * `/` and `/home` land on Today.
 */
export const shellPath = (path, pathname = typeof window !== 'undefined' ? window.location.pathname : '') => {
	if (typeof path !== 'string' || !isMaestroV2Path(pathname)) return path
	if (path === '/' || path === '/home') return '/v2/today'
	if (isMaestroV2Path(path)) return path
	const { bare, query, hash } = splitPath(path)
	const match = byLength('classic').find((row) => onBoundary(bare, row.classic))
	if (!match) return path
	return `${match.v2}${bare.slice(match.classic.length)}${query}${hash}`
}

/** The base a route parser should use: the V2 prefix when the URL is already under it. */
export const v2BaseFor = (classicBase, pathname = '') => {
	const match = HOSTED.find((row) => row.classic === classicBase)
	if (!match) return classicBase
	const { bare } = splitPath(pathname)
	return onBoundary(bare, match.v2) ? match.v2 : classicBase
}

/** Classic URL for a hosted V2 screen, or null when V2 is not hosting this path. */
export const classicHostedPath = (pathname = '') => {
	const { bare, query, hash } = splitPath(pathname)
	const match = byLength('v2').find((row) => onBoundary(bare, row.v2))
	if (!match) return null
	return `${match.classic}${bare.slice(match.v2.length)}${query}${hash}`
}

/** V2 URL for a hosted Classic screen, or null when V2 has no host for it. */
export const v2HostedPath = (pathname = '') => {
	const { bare, query, hash } = splitPath(pathname)
	const match = byLength('classic').find((row) => onBoundary(bare, row.classic))
	if (!match) return null
	return `${match.v2}${bare.slice(match.classic.length)}${query}${hash}`
}
